import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

/**
 * Utility class for exporting dashboard components to PDF
 * Production-ready with error handling and optimization
 */
class PDFExporter {
  /**
   * Export a dashboard component to PDF
   * @param {string} elementId - ID of the element to export
   * @param {string} filename - Name of the PDF file
   * @param {Object} options - Configuration options
   */
  static async exportToPDF(elementId, filename = 'dashboard-report', options = {}) {
    try {
      const marginMm = options.marginMm != null ? Number(options.marginMm) : 0;
      const { marginMm: _omit, ...restOptions } = options;

      // Default options
      const defaultOptions = {
        scale: 2, // Higher resolution for better quality
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        removeContainer: true,
        imageTimeout: 15000,
        logging: false,
        width: null,
        height: null,
        scrollX: 0,
        scrollY: 0,
        ...restOptions
      };

      // Show loading indicator
      const loadingElement = this.showLoading();

      // Get the element to export
      const element = document.getElementById(elementId);
      if (!element) {
        throw new Error(`Element with ID '${elementId}' not found`);
      }

      // Create a temporary container to avoid affecting the original layout
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '0';
      tempContainer.style.width = '1200px'; // Fixed width for consistent layout
      tempContainer.style.backgroundColor = '#ffffff';
      // Page margins: add padding so exported content has proper layout (A4 210mm ~ 1200px => ~5.71px/mm)
      if (marginMm > 0) {
        const paddingPx = Math.round(marginMm * 5.71);
        tempContainer.style.padding = `${paddingPx}px`;
        tempContainer.style.boxSizing = 'border-box';
      }
      document.body.appendChild(tempContainer);

      // Clone the element
      const clonedElement = element.cloneNode(true);
      
      // Apply styles for better PDF rendering
      this.optimizeForPDF(clonedElement);
      
      tempContainer.appendChild(clonedElement);

      // Wait for images and charts to load
      await this.waitForImages(tempContainer);
      await this.waitForCharts(tempContainer);

      // Generate canvas from HTML
      const canvas = await html2canvas(tempContainer, defaultOptions);

      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210;
      const pageHeight = 297;

      let contentWidth = pageWidth;
      let contentHeight = pageHeight;
      let offsetX = 0;
      let offsetY = 0;

      if (marginMm > 0) {
        contentWidth = pageWidth - 2 * marginMm;
        contentHeight = pageHeight - 2 * marginMm;
        offsetX = marginMm;
        offsetY = marginMm;
      }

      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      // Add pages with margin-aware layout
      pdf.addImage(imgData, 'PNG', offsetX, offsetY + position, imgWidth, imgHeight);
      heightLeft -= contentHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', offsetX, offsetY + position, imgWidth, imgHeight);
        heightLeft -= contentHeight;
      }

      // Add metadata
      const currentDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

      pdf.setProperties({
        title: `${filename} - ${currentDate}`,
        subject: 'Dashboard Report',
        author: 'BAS System',
        keywords: 'dashboard, report, telecom, regulatory',
        creator: 'BAS Dashboard System'
      });

      // Clean up
      document.body.removeChild(tempContainer);
      this.hideLoading(loadingElement);

      // Save the PDF
      pdf.save(`${filename}_${new Date().toISOString().split('T')[0]}.pdf`);

      return { success: true, message: 'Report exported successfully' };

    } catch (error) {
      console.error('PDF export error:', error);
      this.hideLoading();
      return { success: false, error: error.message };
    }
  }

  /**
   * Optimize cloned element for PDF rendering
   */
  static optimizeForPDF(element) {
    // Remove any animations
    element.style.animation = 'none';
    element.style.transition = 'none';

    // Ensure proper background
    element.style.backgroundColor = '#ffffff';
    element.style.color = '#000000';

    // Fix any layout issues
    const allElements = element.querySelectorAll('*');
    allElements.forEach(el => {
      el.style.animation = 'none';
      el.style.transition = 'none';
      
      // Ensure charts are visible
      if (el.tagName === 'CANVAS') {
        el.style.maxWidth = 'none';
        el.style.width = 'auto';
        el.style.height = 'auto';
      }

      // Fix text rendering
      if (el.style.color === 'transparent' || el.style.opacity === '0') {
        el.style.color = '#000000';
        el.style.opacity = '1';
      }
    });
  }

  /**
   * Wait for images to load
   */
  static waitForImages(container) {
    return new Promise(resolve => {
      const images = container.querySelectorAll('img');
      if (images.length === 0) {
        resolve();
        return;
      }

      let loadedCount = 0;
      const totalImages = images.length;

      const checkComplete = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          resolve();
        }
      };

      images.forEach(img => {
        if (img.complete) {
          checkComplete();
        } else {
          img.addEventListener('load', checkComplete);
          img.addEventListener('error', checkComplete);
        }
      });

      // Timeout fallback
      setTimeout(resolve, 5000);
    });
  }

  /**
   * Wait for charts to render
   */
  static waitForCharts(container) {
    return new Promise(resolve => {
      const canvases = container.querySelectorAll('canvas');
      if (canvases.length === 0) {
        resolve();
        return;
      }

      // Give charts time to render
      setTimeout(resolve, 2000);
    });
  }

  /**
   * Show loading indicator
   */
  static showLoading() {
    const loading = document.createElement('div');
    loading.id = 'pdf-loading';
    loading.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
      color: white;
      font-family: Arial, sans-serif;
      font-size: 18px;
    `;
    loading.innerHTML = `
      <div style="text-align: center;">
        <div style="border: 4px solid #f3f3f3; border-top: 4px solid #3498db; border-radius: 50%; width: 50px; height: 50px; animation: spin 2s linear infinite; margin: 0 auto 20px;"></div>
        <div>Generating PDF Report...</div>
        <div style="font-size: 14px; margin-top: 10px; opacity: 0.8;">Please wait while we prepare your report</div>
      </div>
      <style>
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      </style>
    `;
    document.body.appendChild(loading);
    return loading;
  }

  /**
   * Hide loading indicator
   */
  static hideLoading(loadingElement = null) {
    const loading = loadingElement || document.getElementById('pdf-loading');
    if (loading) {
      document.body.removeChild(loading);
    }
  }

  /**
   * Export multiple dashboards to a single PDF
   */
  static async exportMultipleToPDF(elementIds, filename = 'combined-dashboard-report', options = {}) {
    try {
      const loadingElement = this.showLoading();
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      let isFirstPage = true;

      for (const elementId of elementIds) {
        const element = document.getElementById(elementId);
        if (!element) {
          console.warn(`Element with ID '${elementId}' not found, skipping...`);
          continue;
        }

        const tempContainer = document.createElement('div');
        tempContainer.style.position = 'absolute';
        tempContainer.style.left = '-9999px';
        tempContainer.style.top = '0';
        tempContainer.style.width = '1200px';
        tempContainer.style.backgroundColor = '#ffffff';
        document.body.appendChild(tempContainer);

        const clonedElement = element.cloneNode(true);
        this.optimizeForPDF(clonedElement);
        tempContainer.appendChild(clonedElement);

        await this.waitForImages(tempContainer);
        await this.waitForCharts(tempContainer);

        const canvas = await html2canvas(tempContainer, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          ...options
        });

        const imgData = canvas.toDataURL('image/png');
        const imgWidth = 210;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        if (!isFirstPage) {
          pdf.addPage();
        }

        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
        isFirstPage = false;

        document.body.removeChild(tempContainer);
      }

      // Add metadata
      const currentDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

      pdf.setProperties({
        title: `${filename} - ${currentDate}`,
        subject: 'Combined Dashboard Report',
        author: 'BAS System',
        keywords: 'dashboard, report, telecom, regulatory, combined',
        creator: 'BAS Dashboard System'
      });

      this.hideLoading(loadingElement);
      pdf.save(`${filename}_${new Date().toISOString().split('T')[0]}.pdf`);

      return { success: true, message: 'Combined report exported successfully' };

    } catch (error) {
      console.error('Multi-PDF export error:', error);
      this.hideLoading();
      return { success: false, error: error.message };
    }
  }
}

export default PDFExporter;